import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  activeBookings,
  type Excursion,
  useCancelExcursionMutation,
  useDeleteExcursionMutation,
  useMarkHeldMutation,
  useRotatePublicTokenMutation,
} from '@/entities/excursion'
import { useSession } from '@/entities/session'
import { errorMessage } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { toast } from '@/shared/ui/toast'

interface ExcursionLifecycleProps {
  excursion: Excursion
}

/**
 * Отмена, отметка «состоялся» и удаление. Отмена — обычный переход (погода):
 * причина обязательна, её видит и команда, и тот, кто откроет ссылку, а записи
 * остаются следом, кому звонить. Удаление — уборка ошибочно заведённого: оно
 * стирает и записи, и ссылку, никого не уведомляя, поэтому спрашивает вслух.
 *
 * Всё это администраторское: гид свой выход видит, но не ведёт.
 */
export function ExcursionLifecycle({ excursion }: ExcursionLifecycleProps) {
  const { session } = useSession()
  const navigate = useNavigate()
  const cancel = useCancelExcursionMutation()
  const markHeld = useMarkHeldMutation()
  const rotate = useRotatePublicTokenMutation()
  const remove = useDeleteExcursionMutation()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [heldOpen, setHeldOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [noShow, setNoShow] = useState<string[]>([])

  const bookings = activeBookings(excursion)
  const planned = excursion.state === 'planned'

  const doCancel = (event: React.FormEvent) => {
    event.preventDefault()
    cancel.mutate(
      { id: excursion.id, reason: reason.trim() },
      {
        onSuccess: (result) => {
          setCancelOpen(false)
          setReason('')
          const count = result.clientsToCall.length
          toast.notify(
            count > 0
              ? `Выход отменён. Позвонить: ${result.clientsToCall.map((c) => c.name).join(', ')}`
              : 'Выход отменён',
          )
        },
      },
    )
  }

  const doHold = () => {
    markHeld.mutate(
      { id: excursion.id, noShowBookingIds: noShow },
      {
        onSuccess: (updated) => {
          setHeldOpen(false)
          setNoShow([])
          toast.success(`Отмечено. Пришло мест: ${updated.seatsTaken}`)
        },
      },
    )
  }

  const doDelete = () => {
    remove.mutate(excursion.id, {
      onSuccess: () => {
        setDeleteOpen(false)
        toast.notify('Выход удалён')
        void navigate({ to: '/excursions' })
      },
    })
  }

  if (!session?.isAdmin) return null

  return (
    // Ход выхода — кнопками, уборка — под «ещё». Удаление стирает и записи, и
    // ссылку, никого не уведомляя, а ghost-кнопкой в общем ряду выглядело
    // безобиднее отмены, хотя опаснее её.
    <div className="flex flex-wrap items-center gap-2">
      {planned && (
        <>
          <Button variant="secondary" size="sm" onClick={() => setHeldOpen(true)}>
            Состоялся
          </Button>
          <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
            Отменить
          </Button>
        </>
      )}

      <Menu
        ariaLabel="Ещё действия с выходом"
        triggerClassName="px-1.5 py-1.5 text-text-tertiary hover:text-text-primary"
        trigger={<Icon name="more" size={18} />}
      >
        {(close) => (
          <>
            {planned && (
              <MenuItem
                onSelect={() => {
                  rotate.mutate(excursion.id, {
                    onSuccess: () => toast.success('Ссылка сменилась — прежняя больше не работает'),
                  })
                  close()
                }}
              >
                Сменить ссылку записи
              </MenuItem>
            )}
            <MenuItem
              className="text-accent hover:text-accent"
              onSelect={() => {
                setDeleteOpen(true)
                close()
              }}
            >
              Удалить выход
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Удалить выход"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Оставить
            </Button>
            <Button variant="danger" disabled={remove.isPending} onClick={doDelete}>
              Удалить насовсем
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            «{excursion.title}» исчезнет из расписания вместе со ссылкой, по которой на него
            записывались. Вернуть будет нечего.
          </p>
          {bookings.length > 0 && (
            <p className="text-sm text-warning">
              Сгорит записей: {bookings.length}. Продукт этим людям ничего не сообщит — если выход
              не состоится, а позвонить надо, отмените его вместо удаления.
            </p>
          )}
          {remove.isError && (
            <p role="alert" className="text-sm text-accent">
              {errorMessage(remove.error)}
            </p>
          )}
        </div>
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Отменить выход"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              Не отменять
            </Button>
            <Button
              type="submit"
              form="cancel-form"
              variant="danger"
              disabled={cancel.isPending || reason.trim().length === 0}
            >
              Отменить выход
            </Button>
          </>
        }
      >
        <form id="cancel-form" onSubmit={doCancel} className="flex flex-col gap-4">
          <p className="text-sm">
            Записи останутся — по ним видно, кому звонить. Продукт клиентам не пишет.
          </p>
          <Field label="Причина" hint="Её увидит команда и тот, кто откроет ссылку">
            {(props) => (
              <Input
                {...props}
                value={reason}
                placeholder="Например: гроза"
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          {cancel.isError && (
            <p role="alert" className="text-sm text-accent">
              {errorMessage(cancel.error)}
            </p>
          )}
        </form>
      </Dialog>

      <Dialog
        open={heldOpen}
        onClose={() => setHeldOpen(false)}
        title="Выход состоялся"
        footer={
          <>
            <Button variant="ghost" onClick={() => setHeldOpen(false)}>
              Отмена
            </Button>
            <Button onClick={doHold} disabled={markHeld.isPending}>
              Отметить
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm">Кто не пришёл — снимите отметку, эти записи закроются.</p>
          {bookings.map((booking) => (
            <Checkbox
              key={booking.id}
              checked={!noShow.includes(booking.id)}
              onChange={() =>
                setNoShow((current) =>
                  current.includes(booking.id)
                    ? current.filter((id) => id !== booking.id)
                    : [...current, booking.id],
                )
              }
              label={
                <span className="text-sm">
                  {booking.clientName} · {booking.seats}
                </span>
              }
            />
          ))}
          {bookings.length === 0 && <p className="text-sm text-text-tertiary">Записей нет.</p>}
        </div>
      </Dialog>
    </div>
  )
}
